import type {
  PartnerCompanyChangeType,
  PartnerCompanyFieldChange,
  PartnerCompanyHistoryResponse,
} from '@/entities/partner-company/api/partner-company.api';

/** Figma 업체 상세 「계약 변경 이력」행 */
export interface PartnerCompanyHistoryRow {
  key: string;
  versionLabel: string;
  changeType: string;
  changeContent: string;
  before: string;
  after: string;
  effectiveStartDate: string;
  changedBy: string;
  changedAt: string;
}

const DASH = '-';

/** API 라벨이 없을 때 쓰는 변경 구분 표시값 */
export const PARTNER_COMPANY_CHANGE_TYPE_LABELS: Record<
  PartnerCompanyChangeType,
  string
> = {
  COMPANY_CREATED: '업체 등록',
  COMPANY_INFO_UPDATED: '기본 정보',
  SETTLEMENT_INFO_UPDATED: '정산 정보',
  COMPANY_STATUS_UPDATED: '상태',
  CONTRACT_CREATED: '신규 계약',
  CONTRACT_PERIOD_UPDATED: '계약 기간',
  DOCUMENT_ADDED: '첨부 문서',
  ASSIGNMENT_CREATED: '거래처 연결',
  ASSIGNMENT_CANCELLED: '연결 취소',
};

import { formatDate, formatDateTime } from '@/shared/lib/dateFormat';

const formatDateOnly = (value?: string | null): string => {
  const formatted = formatDate(value);
  return formatted === '-' ? '' : formatted;
};

export const formatPartnerCompanyHistoryDateTime = formatDateTime;

const formatCellValue = (value?: string | null): string => {
  if (value == null || String(value).trim() === '') return DASH;
  return String(value).trim();
};

/**
 * 한 이력 행의 변경 전/후 셀.
 * 서버 `PartnerCompanyChangeDescriber` 문장을 그대로 쓰고, 항목이 여러 개면 줄바꿈한다.
 */
const formatChangeCell = (
  changes: PartnerCompanyFieldChange[] | undefined,
  side: 'before' | 'after',
): string => {
  if (!changes?.length) return DASH;
  return changes.map((change) => formatCellValue(change[side])).join('\n');
};

const formatChangeContent = (
  changes: PartnerCompanyFieldChange[] | undefined,
): string => {
  if (!changes?.length) return DASH;
  const labels = changes.map((change) => change.label?.trim() || DASH);
  return labels.join('\n');
};

const resolveChangeTypeLabel = (
  history: PartnerCompanyHistoryResponse,
): string => {
  const fromApi = history.changeTypeLabel?.trim();
  if (fromApi) return fromApi;
  return (
    PARTNER_COMPANY_CHANGE_TYPE_LABELS[history.changeType] ??
    history.changeType ??
    DASH
  );
};

export const mapPartnerCompanyHistoryToRow = (
  history: PartnerCompanyHistoryResponse,
): PartnerCompanyHistoryRow => ({
  key: String(
    history.partnerCompanyHistoryId ??
      `${history.revision}-${history.changedAt ?? ''}`,
  ),
  versionLabel:
    history.revision == null ? DASH : `v${history.revision}`,
  changeType: resolveChangeTypeLabel(history),
  changeContent: formatChangeContent(history.changes),
  before: formatChangeCell(history.changes, 'before'),
  after: formatChangeCell(history.changes, 'after'),
  effectiveStartDate: formatDateOnly(history.effectiveDate) || DASH,
  changedBy: history.changedByName?.trim() || DASH,
  changedAt: formatPartnerCompanyHistoryDateTime(history.changedAt),
});

export const mapPartnerCompanyHistoriesToRows = (
  histories: PartnerCompanyHistoryResponse[] | null | undefined,
): PartnerCompanyHistoryRow[] => (histories ?? []).map(mapPartnerCompanyHistoryToRow);
